import {
  Dropdown,
  makeStyles,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Spinner,
  useId,
} from "@fluentui/react-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetFilters } from "../../hooks/useSearchFilters";
import { QueryFilter } from "../../models/QueryFilter";
import {
  SearchFilter,
  SearchFilterValue,
} from "../../models/SearchConfiguration";
import { QueryFilterTags } from "./QueryFilterTags";
import {
  cloneFilters,
  getUniqueFilterValues,
  normalizeFilterValue,
} from "./filterValueUtils";
import { SearchFilterCard } from "./SearchFilterCard";

interface SearchFiltersProps {
  endpoint: string;
  selectedFilterValues: QueryFilter[];
  onFiltersChange: (selectedFilters: QueryFilter[]) => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    gap: "50px",
  },
  filterCardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    flexWrap: "wrap",
  },
  filterDropdown: {
    minWidth: "500px",
    maxWidth: "500px",
  },
  selectedFiltersContainer: {
    maxHeight: "500px",
    minWidth: "500px",
    maxWidth: "500px",
    overflowY: "auto",
  },
});

export const SearchFilters = (props: SearchFiltersProps) => {
  const classes = useStyles();
  const { endpoint, selectedFilterValues, onFiltersChange } = props;
  const { t } = useTranslation();
  const { data: searchFilters, isLoading: loadingFilters } =
    useGetFilters(endpoint);

  const [selectedFilterName, setSelectedFilterName] = useState<
    string | undefined
  >(undefined);
  const filterSelectorDropdownId = useId("filter-selector");

  const handleFilterValuesChange = (
    fieldUniqueName: string,
    newValues: SearchFilterValue[]
  ) => {
    const newQueryfilters: QueryFilter[] = [];

    selectedFilterValues.forEach((filter) => {
      if (filter.fieldUniqueName !== fieldUniqueName) {
        newQueryfilters.push(filter);
      }
    });

    const newFilter: QueryFilter = {
      fieldUniqueName: fieldUniqueName,
      values: getUniqueFilterValues(fieldUniqueName, newValues).map(
        (searchVal) => searchVal.value
      ),
    };

    if (newFilter.values.length !== 0) {
      newQueryfilters.push(newFilter);
    }

    onFiltersChange(newQueryfilters);
  };

  const onDropdownChange = (_ev: SelectionEvents, data: OptionOnSelectData) => {
    if (!data.optionValue) {
      return;
    }

    const targetFilter = searchFilters?.find(
      (f) => f.fieldUniqueName === data.optionValue
    );

    if (targetFilter) {
      setSelectedFilterName(targetFilter.fieldUniqueName);
    }
  };

  const getSelectedValuesForFilter = (
    filter: SearchFilter
  ): SearchFilterValue[] => {
    const targetFilter = selectedFilterValues.find(
      (f) => f.fieldUniqueName === filter.fieldUniqueName
    );

    if (!targetFilter || !searchFilters) {
      return [];
    }

    const searchFilter = searchFilters.find(
      (f) => f.fieldUniqueName === targetFilter.fieldUniqueName
    );

    const selectedValues = new Set(
      targetFilter.values.map((value) =>
        normalizeFilterValue(targetFilter.fieldUniqueName, value)
      )
    );
    const values = getUniqueFilterValues(
      filter.fieldUniqueName,
      searchFilter?.values ?? []
    ).filter((value) =>
      selectedValues.has(
        normalizeFilterValue(filter.fieldUniqueName, value.value)
      )
    );

    return values;
  };

  if (loadingFilters || !searchFilters) {
    return <Spinner />;
  }

  const selectedFilter =
    searchFilters.find(
      (filter) => filter.fieldUniqueName === selectedFilterName
    ) ?? searchFilters[0];

  return (
    <div className={classes.root}>
      <div className={classes.filterCardContainer}>
        <Dropdown
          key={endpoint}
          id={filterSelectorDropdownId}
          placeholder={t("chooseFilter")}
          size="large"
          onOptionSelect={onDropdownChange}
          className={classes.filterDropdown}
          defaultSelectedOptions={[searchFilters[0].fieldUniqueName]}
          defaultValue={searchFilters[0].fieldDisplayName}
        >
          {searchFilters.map((filter) => (
            <Option key={filter.fieldUniqueName} value={filter.fieldUniqueName}>
              {filter.fieldDisplayName}
            </Option>
          ))}
        </Dropdown>
        {selectedFilter && (
          <SearchFilterCard
            key={`${endpoint}:${selectedFilter.fieldUniqueName}`}
            filter={{
              ...selectedFilter,
              values: cloneFilters(selectedFilter.values),
            }}
            selectedFilterValues={getSelectedValuesForFilter(selectedFilter)}
            onFilterValuesChange={(selectedValues) =>
              handleFilterValuesChange(
                selectedFilter.fieldUniqueName,
                selectedValues
              )
            }
          />
        )}
      </div>
      <div>
        <h2>{t("selectedFiltersHeader")}</h2>
        <div className={classes.selectedFiltersContainer}>
          <QueryFilterTags
            selectedFilters={selectedFilterValues}
            filtersEndpoint={endpoint}
            onFiltersChange={onFiltersChange}
            displayMode="grouped"
            disableQueryParamFiltering
          />
        </div>
      </div>
    </div>
  );
};
